import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LogOut, Check, X, Plus, Edit, Trash2, Calendar as CalendarIcon, Image as ImageIcon, Settings as SettingsIcon, Package, MessageSquare, Lock, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar } from '@/components/ui/calendar';
import { toast } from 'sonner';
import { enAU } from 'date-fns/locale';
import { format } from 'date-fns';
import ImageUpload from '@/components/ImageUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconOptions = ['Sparkles', 'Scissors', 'Brush', 'Droplets', 'Hand'];
const socialPlatforms = ['Instagram', 'Facebook', 'Twitter', 'WhatsApp', 'TikTok'];
const dayNames = {
  monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday',
  thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday'
};
const dayNamesShort = {
  monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed',
  thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun'
};

// Format price in AUD
const formatPrice = (price) => {
  return price.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
};

export default function AdminPanel() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [activeTab, setActiveTab] = useState('requests');
  
  const [services, setServices] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [settings, setSettings] = useState({ socialMedia: [] });
  const [offDays, setOffDays] = useState([]);
  const [products, setProducts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [giftCardOptions, setGiftCardOptions] = useState([]);
  const [giftCardPurchases, setGiftCardPurchases] = useState([]);
  
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [blockData, setBlockData] = useState({ time: '', professional: '', reason: '' });
  
  const [editServiceDialog, setEditServiceDialog] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState({ name: '', description: '', price: '', duration: '', icon: 'Sparkles', imageUrl: '', durations: [] });
  
  const [editProfessionalDialog, setEditProfessionalDialog] = useState(false);
  const [editingProfessional, setEditingProfessional] = useState(null);
  const [professionalForm, setProfessionalForm] = useState({ name: '', specialty: '', imageUrl: '' });
  
  const [addProfessionalDialog, setAddProfessionalDialog] = useState(false);
  const [newProfessionalForm, setNewProfessionalForm] = useState({ name: '', specialty: '', imageUrl: '' });
  
  const [addGalleryDialog, setAddGalleryDialog] = useState(false);
  const [galleryForm, setGalleryForm] = useState({ imageUrl: '', description: '', type: 'image' });
  
  const [socialForm, setSocialForm] = useState({ platform: 'Instagram', url: '', icon: 'Instagram' });
  
  // Product state
  const [editProductDialog, setEditProductDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({ name: '', description: '', price: '', imageUrl: '' });
  
  // Testimonial state
  const [editTestimonialDialog, setEditTestimonialDialog] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [testimonialForm, setTestimonialForm] = useState({ quote: '', author: '' });
  
  // Gift card option state
  const [editGiftCardDialog, setEditGiftCardDialog] = useState(false);
  const [editingGiftCardOption, setEditingGiftCardOption] = useState(null);
  const [giftCardOptionForm, setGiftCardOptionForm] = useState({ amount: '', imageUrl: '', enabled: true, isCustom: false });
  
  const intervalRef = useRef(null);

  useEffect(() => {
    if (isAuthenticated) {
      loadData();
      if (activeTab === 'requests') {
        intervalRef.current = setInterval(() => {
          loadBookings();
        }, 5000);
      }
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isAuthenticated, activeTab]);

  const loadData = async () => {
    try {
      const [servicesRes, professionalsRes, bookingsRes, blockedRes, galleryRes, settingsRes, offDaysRes, productsRes, testimonialsRes, giftCardOptionsRes, giftCardPurchasesRes] = await Promise.all([
        axios.get(`${API}/services`),
        axios.get(`${API}/professionals`),
        axios.get(`${API}/bookings`),
        axios.get(`${API}/blocked-slots`),
        axios.get(`${API}/gallery`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/off-days`),
        axios.get(`${API}/products`),
        axios.get(`${API}/testimonials`),
        axios.get(`${API}/gift-cards/options`),
        axios.get(`${API}/gift-cards/purchases`)
      ]);
      setServices(servicesRes.data);
      setProfessionals(professionalsRes.data);
      setBookings(bookingsRes.data);
      setBlockedSlots(blockedRes.data);
      setGallery(galleryRes.data);
      setSettings(settingsRes.data);
      setOffDays(offDaysRes.data);
      setProducts(productsRes.data);
      setTestimonials(testimonialsRes.data);
      setGiftCardOptions(giftCardOptionsRes.data);
      setGiftCardPurchases(giftCardPurchasesRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };
  
  const loadBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`);
      setBookings(response.data);
    } catch (error) {
      console.error('Error loading bookings:', error);
    }
  };

  const handleLogin = async () => {
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        setIsAuthenticated(true);
        toast.success('Logged in successfully');
      }
    } catch (error) {
      toast.error('Incorrect password');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword('');
    toast.info('Logged out');
  };

  const handleApproveBooking = async (bookingId) => {
    try {
      await axios.patch(`${API}/bookings/${bookingId}`, { status: 'approved' });
      toast.success('Booking approved');
      loadData();
    } catch (error) {
      toast.error('Error approving booking');
    }
  };

  const handleRejectBooking = async (bookingId) => {
    try {
      await axios.patch(`${API}/bookings/${bookingId}`, { status: 'rejected' });
      toast.success('Booking rejected');
      loadData();
    } catch (error) {
      toast.error('Error rejecting booking');
    }
  };

  const handleNoShow = async (bookingId) => {
    if (!window.confirm('Mark as No-Show? The client will be charged 50%.')) return;
    try {
      const response = await axios.post(`${API}/payments/charge-no-show/${bookingId}`);
      toast.success(response.data.message);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error processing no-show');
    }
  };

  const handleCompleted = async (bookingId) => {
    if (!window.confirm('Mark as Completed? Full payment will be kept.')) return;
    try {
      await axios.patch(`${API}/bookings/${bookingId}`, { status: 'completed' });
      toast.success('Booking marked as completed');
      loadData();
    } catch (error) {
      toast.error('Error updating booking');
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('Cancel this booking? A refund may be processed.')) return;
    try {
      await axios.patch(`${API}/bookings/${bookingId}`, { status: 'cancelled' });
      toast.success('Booking cancelled');
      loadData();
    } catch (error) {
      toast.error('Error cancelling booking');
    }
  };

  const handleBlockSlot = async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      await axios.post(`${API}/blocked-slots`, {
        date: dateStr,
        time: blockData.time,
        professionalName: blockData.professional,
        reason: blockData.reason
      });
      toast.success('Time slot blocked');
      setBlockDialogOpen(false);
      setBlockData({ time: '', professional: '', reason: '' });
      loadData();
    } catch (error) {
      toast.error('Error blocking time slot');
    }
  };

  const handleUnblockSlot = async (slotId) => {
    try {
      await axios.delete(`${API}/blocked-slots/${slotId}`);
      toast.success('Time slot unblocked');
      loadData();
    } catch (error) {
      toast.error('Error unblocking slot');
    }
  };

  const handleSaveService = async () => {
    try {
      const data = {
        name: serviceForm.name,
        description: serviceForm.description,
        price: parseInt(serviceForm.price),
        duration: parseInt(serviceForm.duration),
        icon: serviceForm.icon,
        imageUrl: serviceForm.imageUrl || '',
        durations: serviceForm.durations || []
      };

      if (editingService) {
        await axios.put(`${API}/services/${editingService.id}`, data);
        toast.success('Service updated');
      } else {
        await axios.post(`${API}/services`, data);
        toast.success('Service created');
      }
      
      setEditServiceDialog(false);
      setEditingService(null);
      setServiceForm({ name: '', description: '', price: '', duration: '', icon: 'Sparkles', imageUrl: '', durations: [] });
      loadData();
    } catch (error) {
      toast.error('Error saving service');
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    try {
      await axios.delete(`${API}/services/${serviceId}`);
      toast.success('Service deleted');
      loadData();
    } catch (error) {
      toast.error('Error deleting service');
    }
  };

  const handleToggleServiceForProfessional = async (professionalId, serviceName) => {
    const professional = professionals.find(p => p.id === professionalId);
    const hasService = professional.allowedServices.includes(serviceName);
    
    const updatedServices = hasService
      ? professional.allowedServices.filter(s => s !== serviceName)
      : [...professional.allowedServices, serviceName];

    try {
      await axios.put(`${API}/professionals/${professionalId}`, {
        name: professional.name,
        specialty: professional.specialty,
        imageUrl: professional.imageUrl,
        allowedServices: updatedServices,
        workingHours: professional.workingHours
      });
      toast.success('Services updated');
      loadData();
    } catch (error) {
      toast.error('Error updating services');
    }
  };
  
  const handleSaveProfessional = async () => {
    if (!editingProfessional) return;
    
    try {
      await axios.put(`${API}/professionals/${editingProfessional.id}`, {
        name: professionalForm.name,
        specialty: professionalForm.specialty,
        imageUrl: professionalForm.imageUrl,
        allowedServices: editingProfessional.allowedServices,
        workingHours: editingProfessional.workingHours
      });
      toast.success('Professional updated');
      setEditProfessionalDialog(false);
      setEditingProfessional(null);
      setProfessionalForm({ name: '', specialty: '', imageUrl: '' });
      loadData();
    } catch (error) {
      toast.error('Error updating professional');
    }
  };
  
  const handleAddProfessional = async () => {
    if (!newProfessionalForm.name || !newProfessionalForm.specialty || !newProfessionalForm.imageUrl) {
      toast.error('Please fill all fields');
      return;
    }
    
    try {
      await axios.post(`${API}/professionals`, {
        name: newProfessionalForm.name,
        specialty: newProfessionalForm.specialty,
        imageUrl: newProfessionalForm.imageUrl,
        allowedServices: []
      });
      toast.success('Professional added');
      setAddProfessionalDialog(false);
      setNewProfessionalForm({ name: '', specialty: '', imageUrl: '' });
      loadData();
    } catch (error) {
      toast.error('Error adding professional');
    }
  };
  
  const handleDeleteProfessional = async (professionalId) => {
    if (!window.confirm('Are you sure you want to delete this professional? All their bookings and settings will be removed.')) return;
    
    try {
      await axios.delete(`${API}/professionals/${professionalId}`);
      toast.success('Professional deleted');
      loadData();
    } catch (error) {
      toast.error('Error deleting professional');
    }
  };
  
  const handleAddGalleryImage = async () => {
    try {
      await axios.post(`${API}/gallery`, galleryForm);
      toast.success('Content added to gallery');
      setAddGalleryDialog(false);
      setGalleryForm({ imageUrl: '', description: '', type: 'image' });
      loadData();
    } catch (error) {
      toast.error('Error adding content');
    }
  };
  
  const handleDeleteGalleryImage = async (imageId) => {
    if (!window.confirm('Delete this content from gallery?')) return;
    try {
      await axios.delete(`${API}/gallery/${imageId}`);
      toast.success('Content deleted');
      loadData();
    } catch (error) {
      toast.error('Error deleting content');
    }
  };
  
  const handleAddSocialMedia = async () => {
    try {
      const newSocialMedia = [...settings.socialMedia, { 
        id: `social-${Date.now()}`, 
        ...socialForm 
      }];
      await axios.put(`${API}/settings`, { 
        ...settings, 
        socialMedia: newSocialMedia 
      });
      toast.success('Social media added');
      setSocialForm({ platform: 'Instagram', url: '', icon: 'Instagram' });
      loadData();
    } catch (error) {
      toast.error('Error adding social media');
    }
  };
  
  const handleDeleteSocialMedia = async (socialId) => {
    try {
      const newSocialMedia = settings.socialMedia.filter(s => s.id !== socialId);
      await axios.put(`${API}/settings`, { 
        ...settings, 
        socialMedia: newSocialMedia 
      });
      toast.success('Social media deleted');
      loadData();
    } catch (error) {
      toast.error('Error deleting social media');
    }
  };
  
  const handleAddClosedDate = async (date) => {
    try {
      await axios.post(`${API}/settings/closed-dates`, { date });
      toast.success('Closed date added');
      loadData();
    } catch (error) {
      toast.error('Error adding closed date');
    }
  };
  
  const handleRemoveClosedDate = async (date) => {
    try {
      await axios.delete(`${API}/settings/closed-dates/${date}`);
      toast.success('Closed date removed');
      loadData();
    } catch (error) {
      toast.error('Error removing closed date');
    }
  };
  
  const handleAddOffDay = async (professionalName, date, reason) => {
    try {
      await axios.post(`${API}/off-days`, { professionalName, date, reason });
      toast.success('Day off added');
      loadData();
    } catch (error) {
      toast.error('Error adding day off');
    }
  };
  
  const handleDeleteOffDay = async (offDayId) => {
    try {
      await axios.delete(`${API}/off-days/${offDayId}`);
      toast.success('Day off deleted');
      loadData();
    } catch (error) {
      toast.error('Error deleting day off');
    }
  };

  // Product handlers
  const handleSaveProduct = async () => {
    try {
      const data = {
        name: productForm.name,
        description: productForm.description,
        price: parseInt(productForm.price),
        imageUrl: productForm.imageUrl || ''
      };

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, data);
        toast.success('Product updated');
      } else {
        await axios.post(`${API}/products`, data);
        toast.success('Product created');
      }
      
      setEditProductDialog(false);
      setEditingProduct(null);
      setProductForm({ name: '', description: '', price: '', imageUrl: '' });
      loadData();
    } catch (error) {
      toast.error('Error saving product');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await axios.delete(`${API}/products/${productId}`);
      toast.success('Product deleted');
      loadData();
    } catch (error) {
      toast.error('Error deleting product');
    }
  };

  // Testimonial handlers
  const handleSaveTestimonial = async () => {
    try {
      if (editingTestimonial) {
        await axios.put(`${API}/testimonials/${editingTestimonial.id}`, {
          quote: testimonialForm.quote,
          author: testimonialForm.author,
          isFixed: editingTestimonial.isFixed || false
        });
        toast.success('Testimonial updated');
      } else {
        await axios.post(`${API}/testimonials`, {
          quote: testimonialForm.quote,
          author: testimonialForm.author,
          isFixed: false
        });
        toast.success('Testimonial created');
      }
      
      setEditTestimonialDialog(false);
      setEditingTestimonial(null);
      setTestimonialForm({ quote: '', author: '' });
      loadData();
    } catch (error) {
      toast.error('Error saving testimonial');
    }
  };

  const handleDeleteTestimonial = async (testimonialId, isFixed) => {
    if (isFixed) {
      toast.error('Cannot delete fixed Google Maps reviews');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;
    try {
      await axios.delete(`${API}/testimonials/${testimonialId}`);
      toast.success('Testimonial deleted');
      loadData();
    } catch (error) {
      toast.error('Error deleting testimonial');
    }
  };

  // Gift Card handlers
  const handleMarkGiftCardPickedUp = async (giftCardId) => {
    if (!window.confirm('Mark this gift card as picked up?')) return;
    try {
      await axios.patch(`${API}/gift-cards/pick-up/${giftCardId}`);
      toast.success('Gift card marked as picked up');
      loadData();
    } catch (error) {
      toast.error('Error updating gift card');
    }
  };

  const handleToggleGiftCardOption = async (optionId, enabled) => {
    try {
      const option = giftCardOptions.find(o => o.id === optionId);
      await axios.put(`${API}/gift-cards/options/${optionId}`, {
        ...option,
        enabled: !enabled
      });
      toast.success('Gift card option updated');
      loadData();
    } catch (error) {
      toast.error('Error updating option');
    }
  };

  const handleSaveGiftCardOption = async () => {
    try {
      if (!editingGiftCardOption) return;
      
      const data = {
        amount: editingGiftCardOption.isCustom ? 0 : parseInt(giftCardOptionForm.amount),
        imageUrl: giftCardOptionForm.imageUrl,
        enabled: giftCardOptionForm.enabled,
        isCustom: editingGiftCardOption.isCustom
      };

      await axios.put(`${API}/gift-cards/options/${editingGiftCardOption.id}`, data);
      toast.success('Gift card option updated');
      setEditGiftCardDialog(false);
      setEditingGiftCardOption(null);
      setGiftCardOptionForm({ amount: '', imageUrl: '', enabled: true, isCustom: false });
      loadData();
    } catch (error) {
      toast.error('Error saving gift card option');
    }
  };

  const generateTimeSlots = () => {
    const slots = [];
    const startHour = 9;
    const endHour = selectedDate?.getDay() === 6 ? 15 : 20;
    
    for (let hour = startHour; hour < endHour; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
      if (hour < endHour - 1 || (hour === endHour - 1 && selectedDate?.getDay() !== 6)) {
        slots.push(`${hour.toString().padStart(2, '0')}:30`);
      }
    }
    return slots;
  };

  const getSlotStatus = (time, professionalName) => {
    if (!selectedDate || isNaN(selectedDate.getTime())) return { status: 'available' };
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    
    const blocked = blockedSlots.find(
      s => s.date === dateStr && s.time === time && s.professionalName === professionalName
    );
    if (blocked) return { status: 'blocked', data: blocked };
    
    // Check for approved, completed or no_show bookings first (they take priority visually)
    const approvedBooking = bookings.find(
      b => b.date === dateStr && b.time === time && b.professionalName === professionalName && 
      (b.status === 'approved' || b.status === 'aprobado' || b.status === 'completed' || b.status === 'no_show')
    );
    if (approvedBooking) return { status: 'approved', data: approvedBooking };
    
    // Then check for pending bookings
    const pendingBooking = bookings.find(
      b => b.date === dateStr && b.time === time && b.professionalName === professionalName && 
      (b.status === 'pending' || b.status === 'pendiente')
    );
    if (pendingBooking) return { status: 'pending', data: pendingBooking };
    
    return { status: 'available' };
  };

  const pendingBookings = bookings.filter(b => b.status === 'pending');

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#faf8f5] flex items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-2xl elegant-script text-center" data-testid="admin-login-title">Admin Panel</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="admin-password">Password</Label>
              <Input 
                id="admin-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                data-testid="admin-password-input"
              />
            </div>
            <Button className="w-full bg-black text-[#e8dcc8]" onClick={handleLogin} data-testid="admin-login-btn">
              Login
            </Button>
            <Button variant="outline" className="w-full" onClick={() => navigate('/')} data-testid="admin-back-home-btn">
              Back to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf8f5] py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold elegant-script" data-testid="admin-panel-title">Admin Panel</h1>
          <Button variant="outline" onClick={handleLogout} data-testid="logout-btn">
            <LogOut className="mr-2" /> Logout
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="admin-tabs">
          <TabsList className="flex flex-wrap gap-1 h-auto mb-8">
            <TabsTrigger value="requests" className="text-xs sm:text-sm" data-testid="tab-requests">
              Requests {pendingBookings.length > 0 && `(${pendingBookings.length})`}
            </TabsTrigger>
            <TabsTrigger value="schedule" className="text-xs sm:text-sm" data-testid="tab-schedule">Schedule</TabsTrigger>
            <TabsTrigger value="services" className="text-xs sm:text-sm" data-testid="tab-services">Services</TabsTrigger>
            <TabsTrigger value="professionals" className="text-xs sm:text-sm" data-testid="tab-professionals">Team</TabsTrigger>
            <TabsTrigger value="products" className="text-xs sm:text-sm" data-testid="tab-products">Products</TabsTrigger>
            <TabsTrigger value="giftcards" className="text-xs sm:text-sm" data-testid="tab-giftcards">Gift Cards</TabsTrigger>
            <TabsTrigger value="testimonials" className="text-xs sm:text-sm" data-testid="tab-testimonials">Reviews</TabsTrigger>
            <TabsTrigger value="gallery" className="text-xs sm:text-sm" data-testid="tab-gallery">Gallery</TabsTrigger>
            <TabsTrigger value="settings" className="text-xs sm:text-sm" data-testid="tab-settings">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="requests" data-testid="requests-content">
            <Card>
              <CardHeader>
                <CardTitle>Pending Requests</CardTitle>
              </CardHeader>
              <CardContent>
                {pendingBookings.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No pending requests</p>
                ) : (
                  <div className="space-y-4">
                    {pendingBookings.map((booking) => (
                      <Card key={booking.id} className="border-[#e8dcc8]" data-testid={`pending-booking-${booking.id}`}>
                        <CardContent className="p-4">
                          <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h3 className="font-bold text-lg">{booking.serviceName}</h3>
                                {booking.payment_status === 'paid' && (
                                  <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">PAID</span>
                                )}
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm mb-3">
                                <div><span className="text-gray-600">Professional:</span> {booking.professionalName}</div>
                                <div><span className="text-gray-600">Client:</span> {booking.clientName}</div>
                                <div><span className="text-gray-600">Date:</span> {new Date(booking.date + 'T00:00:00').toLocaleDateString('en-AU')}</div>
                                <div><span className="text-gray-600">Time:</span> {booking.time}</div>
                                <div><span className="text-gray-600">Email:</span> {booking.clientEmail}</div>
                                <div><span className="text-gray-600">Phone:</span> {booking.clientPhone}</div>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <Button 
                                size="sm" 
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => handleApproveBooking(booking.id)}
                                data-testid={`approve-booking-${booking.id}`}
                              >
                                <Check className="mr-1" /> Approve
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => handleRejectBooking(booking.id)}
                                data-testid={`reject-booking-${booking.id}`}
                              >
                                <X className="mr-1" /> Reject
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
                
                {/* Approved bookings with No-Show option */}
                {bookings.filter(b => b.status === 'approved' && b.payment_status === 'paid').length > 0 && (
                  <div className="mt-8">
                    <h3 className="font-bold text-lg mb-4">Approved & Paid Bookings</h3>
                    <div className="space-y-4">
                      {bookings.filter(b => b.status === 'approved' && b.payment_status === 'paid').map((booking) => (
                        <Card key={booking.id} className="border-green-200 bg-green-50" data-testid={`approved-booking-${booking.id}`}>
                          <CardContent className="p-4">
                            <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <h3 className="font-bold">{booking.serviceName}</h3>
                                  <span className="bg-green-600 text-white text-xs px-2 py-1 rounded">PAID</span>
                                </div>
                                <div className="text-sm">
                                  <p><span className="text-gray-600">Client:</span> {booking.clientName}</p>
                                  <p><span className="text-gray-600">Date:</span> {new Date(booking.date + 'T00:00:00').toLocaleDateString('en-AU')} at {booking.time}</p>
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <Button 
                                  size="sm" 
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => handleCompleted(booking.id)}
                                >
                                  <Check className="mr-1" size={14} /> Completed
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="border-yellow-500 text-yellow-600 hover:bg-yellow-50"
                                  onClick={() => handleNoShow(booking.id)}
                                >
                                  <DollarSign className="mr-1" size={14} /> No-Show (50%)
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="border-red-500 text-red-500 hover:bg-red-50"
                                  onClick={() => handleCancelBooking(booking.id)}
                                >
                                  <X className="mr-1" size={14} /> Cancel
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="schedule" data-testid="schedule-content">
            <Card>
              <CardHeader>
                <CardTitle>Schedule Management</CardTitle>
              </CardHeader>
              <CardContent>
                {/* Color Legend */}
                <div className="mb-4 flex flex-wrap gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-green-500"></div>
                    <span>Approved</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-yellow-500"></div>
                    <span>Pending</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-red-500"></div>
                    <span>Blocked</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded border border-gray-300"></div>
                    <span>Available</span>
                  </div>
                </div>

                <div className="mb-6">
                  <Label className="mb-2 block">Select date</Label>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    locale={enAU}
                    className="rounded-md border border-[#e8dcc8] bg-white"
                    data-testid="schedule-calendar"
                  />
                </div>

                <div className="space-y-6">
                  {professionals.map((prof) => (
                    <div key={prof.id} data-testid={`schedule-professional-${prof.id}`}>
                      <h3 className="font-bold mb-3">{prof.name}</h3>
                      <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                        {generateTimeSlots().map((time) => {
                          const slotInfo = getSlotStatus(time, prof.name);
                          const isBooked = slotInfo.status === 'approved' || slotInfo.status === 'pending';
                          
                          return (
                            <div key={time} className="relative group">
                              <Button
                                size="sm"
                                variant={slotInfo.status === 'available' ? 'outline' : 'default'}
                                className={`text-xs w-full ${
                                  slotInfo.status === 'blocked' 
                                    ? 'bg-red-500 hover:bg-red-600 text-white' 
                                    : slotInfo.status === 'approved'
                                    ? 'bg-green-500 hover:bg-green-600 text-white'
                                    : slotInfo.status === 'pending'
                                    ? 'bg-yellow-500 hover:bg-yellow-600 text-white'
                                    : ''
                                }`}
                                onClick={() => {
                                  if (slotInfo.status === 'available') {
                                    setBlockData({ time, professional: prof.name, reason: '' });
                                    setBlockDialogOpen(true);
                                  } else if (slotInfo.status === 'blocked') {
                                    handleUnblockSlot(slotInfo.data.id);
                                  }
                                }}
                                data-testid={`slot-${prof.id}-${time.replace(':', '-')}`}
                              >
                                {time}
                              </Button>
                              
                              {/* Tooltip for booked slots with action buttons */}
                              {isBooked && slotInfo.data && (
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-50 pointer-events-auto">
                                  <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg min-w-[180px]">
                                    <p className="font-bold">{slotInfo.data.serviceName}</p>
                                    <p>{slotInfo.data.clientName}</p>
                                    <p className="text-gray-300">{slotInfo.data.clientPhone}</p>
                                    <p className={`mt-1 font-semibold ${slotInfo.status === 'approved' ? 'text-green-400' : 'text-yellow-400'}`}>
                                      {slotInfo.data.payment_status === 'paid' ? '💳 Paid' : slotInfo.status === 'approved' ? '✓ Approved' : '⏳ Pending'}
                                    </p>
                                    {slotInfo.status === 'approved' && (
                                      <div className="flex gap-1 mt-2 pt-2 border-t border-gray-700">
                                        <button onClick={(e) => { e.stopPropagation(); handleCompleted(slotInfo.data.id); }} className="flex-1 bg-green-600 hover:bg-green-700 text-white text-[10px] py-1 px-1 rounded">Done</button>
                                        <button onClick={(e) => { e.stopPropagation(); handleNoShow(slotInfo.data.id); }} className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white text-[10px] py-1 px-1 rounded">50%</button>
                                        <button onClick={(e) => { e.stopPropagation(); handleCancelBooking(slotInfo.data.id); }} className="flex-1 bg-red-600 hover:bg-red-700 text-white text-[10px] py-1 px-1 rounded">Cancel</button>
                                      </div>
                                    )}
                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                                      <div className="border-4 border-transparent border-t-gray-900"></div>
                                    </div>
                                  </div>
                                </div>
                              )}
                              
                              {/* Tooltip for blocked slots */}
                              {slotInfo.status === 'blocked' && slotInfo.data && (
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-50">
                                  <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 whitespace-nowrap shadow-lg">
                                    <p className="font-bold text-red-400">Blocked</p>
                                    <p>{slotInfo.data.reason}</p>
                                    <p className="text-gray-400 text-[10px] mt-1">Click to unblock</p>
                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                                      <div className="border-4 border-transparent border-t-gray-900"></div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="services" data-testid="services-content">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Services Management</CardTitle>
                  <Button 
                    onClick={() => {
                      setEditingService(null);
                      setServiceForm({ name: '', description: '', price: '', duration: '', icon: 'Sparkles', imageUrl: '' });
                      setEditServiceDialog(true);
                    }}
                    data-testid="add-service-btn"
                  >
                    <Plus className="mr-2" /> Add Service
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {services.map((service) => (
                    <Card key={service.id} className="border-[#e8dcc8]" data-testid={`service-item-${service.id}`}>
                      <CardContent className="p-4 flex justify-between items-start">
                        <div>
                          <h3 className="font-bold mb-1">{service.name}</h3>
                          <p className="text-sm text-gray-600 mb-2">{service.description}</p>
                          <div className="flex gap-4 text-sm">
                            <span>{service.duration} min</span>
                            <span className="font-bold">{formatPrice(service.price)}</span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              setEditingService(service);
                              setServiceForm({
                                name: service.name,
                                description: service.description,
                                price: service.price.toString(),
                                duration: service.duration.toString(),
                                icon: service.icon,
                                imageUrl: service.imageUrl || '',
                                durations: service.durations || []
                              });
                              setEditServiceDialog(true);
                            }}
                            data-testid={`edit-service-${service.id}`}
                          >
                            <Edit size={16} />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="destructive"
                            onClick={() => handleDeleteService(service.id)}
                            data-testid={`delete-service-${service.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="professionals" data-testid="professionals-content">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Team Management</CardTitle>
                  <Button 
                    onClick={() => setAddProfessionalDialog(true)}
                    data-testid="add-professional-btn"
                  >
                    <Plus className="mr-2" /> Add Professional
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {professionals.map((prof) => (
                    <Card key={prof.id} className="border-[#e8dcc8]" data-testid={`professional-card-${prof.id}`}>
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex items-center gap-3">
                            <img src={prof.imageUrl} alt={prof.name} className="w-16 h-16 rounded-full object-cover" />
                            <div>
                              <h3 className="font-bold">{prof.name}</h3>
                              <p className="text-sm text-gray-600">{prof.specialty}</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingProfessional(prof);
                                setProfessionalForm({
                                  name: prof.name,
                                  specialty: prof.specialty,
                                  imageUrl: prof.imageUrl
                                });
                                setEditProfessionalDialog(true);
                              }}
                              data-testid={`edit-professional-${prof.id}`}
                            >
                              <Edit size={16} className="mr-1" /> Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteProfessional(prof.id)}
                              data-testid={`delete-professional-${prof.id}`}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label className="font-semibold">Enabled Services:</Label>
                          {services.map((service) => (
                            <div key={service.id} className="flex items-center gap-2">
                              <Checkbox
                                id={`${prof.id}-${service.id}`}
                                checked={prof.allowedServices.includes(service.name)}
                                onCheckedChange={() => handleToggleServiceForProfessional(prof.id, service.name)}
                                data-testid={`checkbox-${prof.id}-${service.id}`}
                              />
                              <Label htmlFor={`${prof.id}-${service.id}`} className="cursor-pointer">
                                {service.name}
                              </Label>
                            </div>
                          ))}
                        </div>
                        <div className="mt-4 space-y-2">
                          <Label className="font-semibold">Working Hours:</Label>
                          <div className="bg-gray-50 p-3 rounded-lg space-y-2">
                            {prof.workingHours && Object.entries(prof.workingHours).map(([day, hours]) => (
                              <div key={day} className="flex items-center gap-2 text-sm">
                                <Checkbox
                                  id={`${prof.id}-${day}`}
                                  checked={hours.enabled}
                                  onCheckedChange={async (checked) => {
                                    const newHours = { ...prof.workingHours, [day]: { ...hours, enabled: checked } };
                                    try {
                                      await axios.put(`${API}/professionals/${prof.id}`, {
                                        ...prof,
                                        workingHours: newHours
                                      });
                                      toast.success('Schedule updated');
                                      loadData();
                                    } catch (error) {
                                      toast.error('Error');
                                    }
                                  }}
                                  data-testid={`prof-${prof.id}-${day}-enabled`}
                                />
                                <Label htmlFor={`${prof.id}-${day}`} className="w-12 text-xs">{dayNamesShort[day]}</Label>
                                <Input
                                  type="time"
                                  value={hours.start}
                                  onChange={(e) => {
                                    const newHours = { ...prof.workingHours, [day]: { ...hours, start: e.target.value } };
                                    const updatedProfs = professionals.map(p => 
                                      p.id === prof.id ? { ...p, workingHours: newHours } : p
                                    );
                                    setProfessionals(updatedProfs);
                                  }}
                                  onBlur={async () => {
                                    try {
                                      await axios.put(`${API}/professionals/${prof.id}`, prof);
                                      toast.success('Schedule saved');
                                    } catch (error) {
                                      toast.error('Error');
                                    }
                                  }}
                                  disabled={!hours.enabled}
                                  className="w-20 h-7 text-xs"
                                />
                                <span className="text-xs">to</span>
                                <Input
                                  type="time"
                                  value={hours.end}
                                  onChange={(e) => {
                                    const newHours = { ...prof.workingHours, [day]: { ...hours, end: e.target.value } };
                                    const updatedProfs = professionals.map(p => 
                                      p.id === prof.id ? { ...p, workingHours: newHours } : p
                                    );
                                    setProfessionals(updatedProfs);
                                  }}
                                  onBlur={async () => {
                                    try {
                                      await axios.put(`${API}/professionals/${prof.id}`, prof);
                                      toast.success('Schedule saved');
                                    } catch (error) {
                                      toast.error('Error');
                                    }
                                  }}
                                  disabled={!hours.enabled}
                                  className="w-20 h-7 text-xs"
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="products" data-testid="products-content">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Products Management</CardTitle>
                  <Button 
                    onClick={() => {
                      setEditingProduct(null);
                      setProductForm({ name: '', description: '', price: '', imageUrl: '' });
                      setEditProductDialog(true);
                    }}
                    data-testid="add-product-btn"
                  >
                    <Plus className="mr-2" /> Add Product
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {products.map((product) => (
                    <Card key={product.id} className="border-[#e8dcc8]" data-testid={`product-item-${product.id}`}>
                      <CardContent className="p-0">
                        {product.imageUrl && (
                          <img src={product.imageUrl} alt={product.name} className="w-full h-40 object-cover rounded-t-lg" />
                        )}
                        <div className="p-4">
                          <h3 className="font-bold mb-1">{product.name}</h3>
                          <p className="text-sm text-gray-600 mb-2">{product.description}</p>
                          <p className="font-bold text-lg mb-3">{formatPrice(product.price)}</p>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="flex-1"
                              onClick={() => {
                                setEditingProduct(product);
                                setProductForm({
                                  name: product.name,
                                  description: product.description,
                                  price: product.price.toString(),
                                  imageUrl: product.imageUrl || ''
                                });
                                setEditProductDialog(true);
                              }}
                              data-testid={`edit-product-${product.id}`}
                            >
                              <Edit size={16} className="mr-1" /> Edit
                            </Button>
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => handleDeleteProduct(product.id)}
                              data-testid={`delete-product-${product.id}`}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                {products.length === 0 && (
                  <p className="text-gray-500 text-center py-8">No products yet. Add your first product!</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="testimonials" data-testid="testimonials-content">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Testimonials & Reviews</CardTitle>
                  <Button 
                    onClick={() => {
                      setEditingTestimonial(null);
                      setTestimonialForm({ quote: '', author: '' });
                      setEditTestimonialDialog(true);
                    }}
                    data-testid="add-testimonial-btn"
                  >
                    <Plus className="mr-2" /> Add Testimonial
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {testimonials.map((testimonial) => (
                    <Card key={testimonial.id} className={`border-[#e8dcc8] ${testimonial.isFixed ? 'bg-amber-50 border-amber-200' : ''}`} data-testid={`testimonial-item-${testimonial.id}`}>
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            {testimonial.isFixed && (
                              <div className="flex items-center gap-2 mb-2">
                                <Lock size={14} className="text-amber-600" />
                                <span className="text-xs text-amber-700 font-semibold">Google Maps Review (Fixed)</span>
                              </div>
                            )}
                            <p className="text-gray-700 italic mb-2">"{testimonial.quote}"</p>
                            <p className="font-bold text-sm">- {testimonial.author}</p>
                          </div>
                          <div className="flex gap-2 ml-4">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => {
                                setEditingTestimonial(testimonial);
                                setTestimonialForm({
                                  quote: testimonial.quote,
                                  author: testimonial.author
                                });
                                setEditTestimonialDialog(true);
                              }}
                              data-testid={`edit-testimonial-${testimonial.id}`}
                            >
                              <Edit size={16} />
                            </Button>
                            {!testimonial.isFixed && (
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => handleDeleteTestimonial(testimonial.id, testimonial.isFixed)}
                                data-testid={`delete-testimonial-${testimonial.id}`}
                              >
                                <Trash2 size={16} />
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                {testimonials.length === 0 && (
                  <p className="text-gray-500 text-center py-8">No testimonials yet. Add your first testimonial!</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="giftcards" data-testid="giftcards-content">
            <div className="space-y-6">
              {/* Gift Card Purchases */}
              <Card>
                <CardHeader>
                  <CardTitle>Gift Card Purchases</CardTitle>
                </CardHeader>
                <CardContent>
                  {giftCardPurchases.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No gift card purchases yet</p>
                  ) : (
                    <div className="space-y-4">
                      {giftCardPurchases.map((purchase) => (
                        <Card 
                          key={purchase.id} 
                          className={`border-2 ${
                            purchase.status === 'picked_up' 
                              ? 'border-green-200 bg-green-50' 
                              : purchase.payment_status === 'paid' 
                              ? 'border-blue-200 bg-blue-50' 
                              : 'border-gray-200'
                          }`}
                          data-testid={`giftcard-purchase-${purchase.id}`}
                        >
                          <CardContent className="p-4">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <h3 className="font-bold text-lg">{formatPrice(purchase.amount)} Gift Card</h3>
                                  <span className={`text-xs px-2 py-1 rounded ${
                                    purchase.status === 'picked_up' 
                                      ? 'bg-green-600 text-white' 
                                      : purchase.payment_status === 'paid' 
                                      ? 'bg-blue-600 text-white' 
                                      : 'bg-gray-400 text-white'
                                  }`}>
                                    {purchase.status === 'picked_up' ? 'PICKED UP' : purchase.payment_status === 'paid' ? 'PAID' : 'PENDING'}
                                  </span>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                                  <div><span className="text-gray-600">Code:</span> <span className="font-mono font-bold">{purchase.code}</span></div>
                                  <div><span className="text-gray-600">Buyer:</span> {purchase.buyerName}</div>
                                  <div><span className="text-gray-600">Email:</span> {purchase.buyerEmail}</div>
                                  <div><span className="text-gray-600">Phone:</span> {purchase.buyerPhone}</div>
                                  <div><span className="text-gray-600">Purchased:</span> {new Date(purchase.createdAt).toLocaleDateString('en-AU')}</div>
                                  {purchase.pickedUpAt && (
                                    <div><span className="text-gray-600">Picked up:</span> {new Date(purchase.pickedUpAt).toLocaleDateString('en-AU')}</div>
                                  )}
                                </div>
                              </div>
                              {purchase.payment_status === 'paid' && purchase.status !== 'picked_up' && (
                                <Button 
                                  size="sm" 
                                  className="bg-green-600 hover:bg-green-700 ml-4"
                                  onClick={() => handleMarkGiftCardPickedUp(purchase.id)}
                                  data-testid={`pickup-giftcard-${purchase.id}`}
                                >
                                  <Check className="mr-1" size={14} /> Mark as Picked Up
                                </Button>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Gift Card Options Configuration */}
              <Card>
                <CardHeader>
                  <CardTitle>Gift Card Amount Options</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {giftCardOptions.map((option) => (
                      <Card key={option.id} className="border-[#e8dcc8]" data-testid={`giftcard-option-${option.id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start gap-4">
                            {/* Image Preview */}
                            {option.imageUrl && (
                              <img 
                                src={option.imageUrl} 
                                alt="Gift card" 
                                className="w-24 h-16 object-cover rounded"
                              />
                            )}
                            
                            <div className="flex-1 flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <Checkbox
                                  id={`option-${option.id}`}
                                  checked={option.enabled}
                                  onCheckedChange={() => handleToggleGiftCardOption(option.id, option.enabled)}
                                  data-testid={`toggle-option-${option.id}`}
                                />
                                <div>
                                  <Label htmlFor={`option-${option.id}`} className="text-lg font-semibold cursor-pointer">
                                    {option.isCustom ? 'Custom Amount (User Choice)' : formatPrice(option.amount)}
                                  </Label>
                                  <p className="text-xs text-gray-500">
                                    {option.imageUrl ? 'Image configured' : 'No image set'}
                                  </p>
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-2">
                                <span className={`text-xs px-3 py-1 rounded ${option.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                                  {option.enabled ? 'Enabled' : 'Disabled'}
                                </span>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setEditingGiftCardOption(option);
                                    setGiftCardOptionForm({
                                      amount: option.amount.toString(),
                                      imageUrl: option.imageUrl || '',
                                      enabled: option.enabled,
                                      isCustom: option.isCustom
                                    });
                                    setEditGiftCardDialog(true);
                                  }}
                                  data-testid={`edit-option-${option.id}`}
                                >
                                  <Edit size={16} />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  <p className="text-sm text-gray-500 mt-4">
                    Click the edit button to upload an image for each gift card option. Toggle to enable/disable them on the website.
                  </p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="gallery" data-testid="gallery-content">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Gallery Management (Images & Videos)</CardTitle>
                  <Button onClick={() => setAddGalleryDialog(true)} data-testid="add-gallery-btn">
                    <Plus className="mr-2" /> Add Content
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {gallery.map((item) => (
                    <div key={item.id} className="relative group" data-testid={`gallery-admin-${item.id}`}>
                      {item.type === 'video' ? (
                        <video 
                          src={item.imageUrl} 
                          className="w-full h-40 object-cover rounded-lg"
                          controls
                        />
                      ) : (
                        <img src={item.imageUrl} alt={item.description} className="w-full h-40 object-cover rounded-lg" />
                      )}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteGalleryImage(item.id)}
                          data-testid={`delete-gallery-${item.id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                      {item.description && (
                        <p className="text-xs text-gray-600 mt-1">{item.description}</p>
                      )}
                      <span className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                        {item.type === 'video' ? 'Video' : 'Image'}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings" data-testid="settings-content">
            <div className="space-y-6">
              {/* Hero Image */}
              <Card>
                <CardHeader>
                  <CardTitle>Hero Image</CardTitle>
                </CardHeader>
                <CardContent>
                  <ImageUpload
                    folder="latinbody"
                    currentImageUrl={settings.heroImageUrl}
                    onUploadComplete={async (url) => {
                      try {
                        await axios.put(`${API}/settings`, { ...settings, heroImageUrl: url });
                        toast.success('Hero image updated');
                        loadData();
                      } catch (error) {
                        toast.error('Error updating image');
                      }
                    }}
                  />
                </CardContent>
              </Card>

              {/* Center Hours */}
              <Card>
                <CardHeader>
                  <CardTitle>Center Hours</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {settings.centerHours && Object.entries(settings.centerHours).map(([day, hours]) => (
                      <div key={day} className="flex items-center gap-4 p-3 border rounded-lg">
                        <Checkbox
                          id={`center-${day}`}
                          checked={hours.enabled}
                          onCheckedChange={async (checked) => {
                            const newHours = { ...settings.centerHours, [day]: { ...hours, enabled: checked } };
                            try {
                              await axios.put(`${API}/settings`, { ...settings, centerHours: newHours });
                              toast.success('Hours updated');
                              loadData();
                            } catch (error) {
                              toast.error('Error updating hours');
                            }
                          }}
                          data-testid={`center-hours-${day}-enabled`}
                        />
                        <Label htmlFor={`center-${day}`} className="w-24 font-semibold">{dayNames[day]}</Label>
                        <Input
                          type="time"
                          value={hours.start}
                          onChange={(e) => {
                            const newHours = { ...settings.centerHours, [day]: { ...hours, start: e.target.value } };
                            setSettings({ ...settings, centerHours: newHours });
                          }}
                          onBlur={async () => {
                            try {
                              await axios.put(`${API}/settings`, settings);
                              toast.success('Hours updated');
                            } catch (error) {
                              toast.error('Error');
                            }
                          }}
                          disabled={!hours.enabled}
                          className="w-32"
                          data-testid={`center-hours-${day}-start`}
                        />
                        <span>to</span>
                        <Input
                          type="time"
                          value={hours.end}
                          onChange={(e) => {
                            const newHours = { ...settings.centerHours, [day]: { ...hours, end: e.target.value } };
                            setSettings({ ...settings, centerHours: newHours });
                          }}
                          onBlur={async () => {
                            try {
                              await axios.put(`${API}/settings`, settings);
                              toast.success('Hours updated');
                            } catch (error) {
                              toast.error('Error');
                            }
                          }}
                          disabled={!hours.enabled}
                          className="w-32"
                          data-testid={`center-hours-${day}-end`}
                        />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Social Media */}
              <Card>
                <CardHeader>
                  <CardTitle>Social Media</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 mb-6">
                    {settings.socialMedia.map((social) => (
                      <Card key={social.id} className="border-[#e8dcc8]" data-testid={`social-item-${social.id}`}>
                        <CardContent className="p-4 flex justify-between items-center">
                          <div>
                            <h3 className="font-bold">{social.platform}</h3>
                            <a href={social.url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">
                              {social.url}
                            </a>
                          </div>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDeleteSocialMedia(social.id)}
                            data-testid={`delete-social-${social.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  
                  <Card className="bg-gray-50">
                    <CardContent className="p-4">
                      <h3 className="font-bold mb-4">Add Social Media</h3>
                      <div className="space-y-3">
                        <div>
                          <Label htmlFor="social-platform">Platform</Label>
                          <select
                            id="social-platform"
                            className="w-full border rounded-md p-2"
                            value={socialForm.platform}
                            onChange={(e) => setSocialForm({ ...socialForm, platform: e.target.value, icon: e.target.value })}
                            data-testid="social-platform-select"
                          >
                            {socialPlatforms.map(platform => (
                              <option key={platform} value={platform}>{platform}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label htmlFor="social-url">URL</Label>
                          <Input
                            id="social-url"
                            value={socialForm.url}
                            onChange={(e) => setSocialForm({ ...socialForm, url: e.target.value })}
                            placeholder="https://instagram.com/username"
                            data-testid="social-url-input"
                          />
                        </div>
                        <Button
                          className="w-full bg-black text-[#e8dcc8]"
                          onClick={handleAddSocialMedia}
                          disabled={!socialForm.url}
                          data-testid="add-social-btn"
                        >
                          <Plus className="mr-2" /> Add
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </CardContent>
              </Card>

              {/* Closed Dates */}
              <Card>
                <CardHeader>
                  <CardTitle>Center Closed Dates</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {settings.closedDates && settings.closedDates.length > 0 ? (
                      <div className="space-y-2">
                        {settings.closedDates.map((date) => (
                          <Card key={date} className="border-[#e8dcc8]">
                            <CardContent className="p-3 flex justify-between items-center">
                              <span className="font-semibold">{new Date(date + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleRemoveClosedDate(date)}
                                data-testid={`remove-closed-date-${date}`}
                              >
                                <Trash2 size={16} />
                              </Button>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No closed dates configured</p>
                    )}
                    
                    <Card className="bg-gray-50">
                      <CardContent className="p-4">
                        <h3 className="font-bold mb-3">Add Closed Date</h3>
                        <div className="flex gap-2">
                          <Input
                            type="date"
                            id="closed-date-input"
                            min={format(new Date(), 'yyyy-MM-dd')}
                            data-testid="closed-date-input"
                          />
                          <Button
                            onClick={() => {
                              const input = document.getElementById('closed-date-input');
                              if (input.value) {
                                handleAddClosedDate(input.value);
                                input.value = '';
                              }
                            }}
                            data-testid="add-closed-date-btn"
                          >
                            <Plus className="mr-2" /> Add
                          </Button>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">Example: Public holidays, center vacations</p>
                      </CardContent>
                    </Card>
                  </div>
                </CardContent>
              </Card>

              {/* Professional Off Days */}
              <Card>
                <CardHeader>
                  <CardTitle>Staff Days Off</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {offDays.length > 0 ? (
                      <div className="space-y-2">
                        {offDays.map((offDay) => (
                          <Card key={offDay.id} className="border-[#e8dcc8]">
                            <CardContent className="p-3 flex justify-between items-center">
                              <div>
                                <p className="font-semibold">{offDay.professionalName}</p>
                                <p className="text-sm text-gray-600">{new Date(offDay.date + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                                {offDay.reason && <p className="text-xs text-gray-500 italic">{offDay.reason}</p>}
                              </div>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDeleteOffDay(offDay.id)}
                                data-testid={`remove-off-day-${offDay.id}`}
                              >
                                <Trash2 size={16} />
                              </Button>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No days off configured</p>
                    )}
                    
                    <Card className="bg-gray-50">
                      <CardContent className="p-4">
                        <h3 className="font-bold mb-3">Add Day Off</h3>
                        <div className="space-y-3">
                          <div>
                            <Label htmlFor="off-day-professional">Professional</Label>
                            <select
                              id="off-day-professional"
                              className="w-full border rounded-md p-2"
                              data-testid="off-day-professional-select"
                            >
                              <option value="">Select professional</option>
                              {professionals.map(prof => (
                                <option key={prof.id} value={prof.name}>{prof.name}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <Label htmlFor="off-day-date">Date</Label>
                            <Input
                              type="date"
                              id="off-day-date"
                              min={format(new Date(), 'yyyy-MM-dd')}
                              data-testid="off-day-date-input"
                            />
                          </div>
                          <div>
                            <Label htmlFor="off-day-reason">Reason (Optional)</Label>
                            <Input
                              id="off-day-reason"
                              placeholder="E.g.: Vacation, personal day"
                              data-testid="off-day-reason-input"
                            />
                          </div>
                          <Button
                            className="w-full"
                            onClick={() => {
                              const profSelect = document.getElementById('off-day-professional');
                              const dateInput = document.getElementById('off-day-date');
                              const reasonInput = document.getElementById('off-day-reason');
                              
                              if (profSelect.value && dateInput.value) {
                                handleAddOffDay(profSelect.value, dateInput.value, reasonInput.value);
                                profSelect.value = '';
                                dateInput.value = '';
                                reasonInput.value = '';
                              } else {
                                toast.error('Select professional and date');
                              }
                            }}
                            data-testid="add-off-day-btn"
                          >
                            <Plus className="mr-2" /> Add Day Off
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Block Slot Dialog */}
      <Dialog open={blockDialogOpen} onOpenChange={setBlockDialogOpen}>
        <DialogContent data-testid="block-slot-dialog" aria-describedby="block-dialog-description">
          <DialogHeader>
            <DialogTitle>Block Time Slot</DialogTitle>
          </DialogHeader>
          <p id="block-dialog-description" className="sr-only">Form to block a specific time slot</p>
          <div className="space-y-4">
            <div>
              <Label>Date</Label>
              <p className="font-semibold">{selectedDate ? format(selectedDate, "d MMMM yyyy", { locale: enAU }) : '-'}</p>
            </div>
            <div>
              <Label>Time</Label>
              <p className="font-semibold">{blockData.time}</p>
            </div>
            <div>
              <Label>Professional</Label>
              <p className="font-semibold">{blockData.professional}</p>
            </div>
            <div>
              <Label htmlFor="block-reason">Reason for blocking</Label>
              <Input 
                id="block-reason"
                value={blockData.reason}
                onChange={(e) => setBlockData({ ...blockData, reason: e.target.value })}
                placeholder="e.g.: Lunch break, Doctor appointment"
                data-testid="block-reason-input"
              />
            </div>
            <Button 
              className="w-full bg-black text-[#e8dcc8]" 
              onClick={handleBlockSlot}
              disabled={!blockData.reason}
              data-testid="confirm-block-btn"
            >
              Block Slot
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Service Edit Dialog */}
      <Dialog open={editServiceDialog} onOpenChange={setEditServiceDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="service-edit-dialog" aria-describedby="service-dialog-description">
          <DialogHeader>
            <DialogTitle>{editingService ? 'Edit Service' : 'New Service'}</DialogTitle>
          </DialogHeader>
          <p id="service-dialog-description" className="sr-only">Form to create or edit a service</p>
          <div className="space-y-4">
            <div>
              <Label htmlFor="service-name">Name</Label>
              <Input 
                id="service-name"
                value={serviceForm.name}
                onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })}
                data-testid="service-name-input"
              />
            </div>
            <div>
              <Label htmlFor="service-description">Description</Label>
              <Input 
                id="service-description"
                value={serviceForm.description}
                onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                data-testid="service-description-input"
              />
            </div>
            <div>
              <Label htmlFor="service-icon">Icon</Label>
              <select 
                id="service-icon"
                className="w-full border rounded-md p-2"
                value={serviceForm.icon}
                onChange={(e) => setServiceForm({ ...serviceForm, icon: e.target.value })}
                data-testid="service-icon-select"
              >
                {iconOptions.map(icon => (
                  <option key={icon} value={icon}>{icon}</option>
                ))}
              </select>
            </div>
            
            {/* Duration Options */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <div className="flex justify-between items-center mb-3">
                <Label className="font-semibold">Duration & Price Options</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const newDurations = [...(serviceForm.durations || []), { duration: 30, price: 50 }];
                    setServiceForm({ ...serviceForm, durations: newDurations });
                  }}
                  data-testid="add-duration-btn"
                >
                  <Plus size={14} className="mr-1" /> Add Option
                </Button>
              </div>
              
              {(!serviceForm.durations || serviceForm.durations.length === 0) ? (
                <div className="space-y-3">
                  <p className="text-sm text-gray-500">Single duration mode:</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Duration (min)</Label>
                      <Input 
                        type="number"
                        value={serviceForm.duration}
                        onChange={(e) => setServiceForm({ ...serviceForm, duration: e.target.value })}
                        data-testid="service-duration-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Price (AUD)</Label>
                      <Input 
                        type="number"
                        value={serviceForm.price}
                        onChange={(e) => setServiceForm({ ...serviceForm, price: e.target.value })}
                        data-testid="service-price-input"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {serviceForm.durations.map((opt, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="flex-1">
                        <Label className="text-xs">Duration (min)</Label>
                        <Input 
                          type="number"
                          value={opt.duration}
                          onChange={(e) => {
                            const newDurations = [...serviceForm.durations];
                            newDurations[idx] = { ...opt, duration: parseInt(e.target.value) || 0 };
                            setServiceForm({ ...serviceForm, durations: newDurations });
                          }}
                        />
                      </div>
                      <div className="flex-1">
                        <Label className="text-xs">Price (AUD)</Label>
                        <Input 
                          type="number"
                          value={opt.price}
                          onChange={(e) => {
                            const newDurations = [...serviceForm.durations];
                            newDurations[idx] = { ...opt, price: parseInt(e.target.value) || 0 };
                            setServiceForm({ ...serviceForm, durations: newDurations });
                          }}
                        />
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        className="mt-5"
                        onClick={() => {
                          const newDurations = serviceForm.durations.filter((_, i) => i !== idx);
                          setServiceForm({ ...serviceForm, durations: newDurations });
                        }}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Label className="mb-2 block">Service Image (Optional)</Label>
              <ImageUpload
                folder="services"
                currentImageUrl={serviceForm.imageUrl}
                onUploadComplete={(url) => setServiceForm({ ...serviceForm, imageUrl: url })}
              />
            </div>
            <Button 
              className="w-full bg-black text-[#e8dcc8]" 
              onClick={handleSaveService}
              disabled={!serviceForm.name || !serviceForm.description || ((!serviceForm.durations || serviceForm.durations.length === 0) && (!serviceForm.price || !serviceForm.duration))}
              data-testid="save-service-btn"
            >
              {editingService ? 'Save Changes' : 'Create Service'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Professional Dialog */}
      <Dialog open={editProfessionalDialog} onOpenChange={setEditProfessionalDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="edit-professional-dialog" aria-describedby="edit-professional-description">
          <DialogHeader>
            <DialogTitle>Edit Professional</DialogTitle>
          </DialogHeader>
          <p id="edit-professional-description" className="sr-only">Form to edit professional details</p>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-prof-name">Full Name</Label>
              <Input
                id="edit-prof-name"
                value={professionalForm.name}
                onChange={(e) => setProfessionalForm({ ...professionalForm, name: e.target.value })}
                data-testid="edit-prof-name-input"
              />
            </div>
            <div>
              <Label htmlFor="edit-prof-specialty">Specialty</Label>
              <Input
                id="edit-prof-specialty"
                value={professionalForm.specialty}
                onChange={(e) => setProfessionalForm({ ...professionalForm, specialty: e.target.value })}
                data-testid="edit-prof-specialty-input"
              />
            </div>
            <div>
              <Label className="mb-2 block">Photo</Label>
              <ImageUpload
                folder="professionals"
                currentImageUrl={professionalForm.imageUrl}
                onUploadComplete={(url) => setProfessionalForm({ ...professionalForm, imageUrl: url })}
              />
            </div>
            <Button
              className="w-full bg-black text-[#e8dcc8]"
              onClick={handleSaveProfessional}
              disabled={!professionalForm.name || !professionalForm.specialty}
              data-testid="save-professional-btn"
            >
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Gallery Content Dialog */}
      <Dialog open={addGalleryDialog} onOpenChange={setAddGalleryDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="add-gallery-dialog" aria-describedby="add-gallery-description">
          <DialogHeader>
            <DialogTitle>Add Content to Gallery</DialogTitle>
          </DialogHeader>
          <p id="add-gallery-description" className="sr-only">Form to add image or video to gallery</p>
          <div className="space-y-4">
            <div>
              <Label>Content Type</Label>
              <div className="flex gap-2 mt-2">
                <Button
                  type="button"
                  size="sm"
                  variant={galleryForm.type === 'image' ? 'default' : 'outline'}
                  onClick={() => setGalleryForm({ ...galleryForm, type: 'image', imageUrl: '' })}
                  data-testid="gallery-type-image"
                >
                  Image
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={galleryForm.type === 'video' ? 'default' : 'outline'}
                  onClick={() => setGalleryForm({ ...galleryForm, type: 'video', imageUrl: '' })}
                  data-testid="gallery-type-video"
                >
                  Video
                </Button>
              </div>
            </div>
            <ImageUpload
              folder="gallery"
              currentImageUrl={galleryForm.imageUrl}
              onUploadComplete={(url) => setGalleryForm({ ...galleryForm, imageUrl: url })}
            />
            <div>
              <Label htmlFor="gallery-description">Description (Optional)</Label>
              <Input
                id="gallery-description"
                value={galleryForm.description}
                onChange={(e) => setGalleryForm({ ...galleryForm, description: e.target.value })}
                placeholder="E.g.: Lash lifting result"
                data-testid="gallery-description-input"
              />
            </div>
            <Button
              className="w-full bg-black text-[#e8dcc8]"
              onClick={handleAddGalleryImage}
              disabled={!galleryForm.imageUrl}
              data-testid="save-gallery-image-btn"
            >
              Add to Gallery
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Professional Dialog */}
      <Dialog open={addProfessionalDialog} onOpenChange={setAddProfessionalDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="add-professional-dialog" aria-describedby="add-professional-description">
          <DialogHeader>
            <DialogTitle>Add New Professional</DialogTitle>
          </DialogHeader>
          <p id="add-professional-description" className="sr-only">Form to add a new professional</p>
          <div className="space-y-4">
            <div>
              <Label htmlFor="new-prof-name">Full Name</Label>
              <Input
                id="new-prof-name"
                value={newProfessionalForm.name}
                onChange={(e) => setNewProfessionalForm({ ...newProfessionalForm, name: e.target.value })}
                placeholder="E.g.: Maria Smith"
                data-testid="new-prof-name-input"
              />
            </div>
            <div>
              <Label htmlFor="new-prof-specialty">Specialty</Label>
              <Input
                id="new-prof-specialty"
                value={newProfessionalForm.specialty}
                onChange={(e) => setNewProfessionalForm({ ...newProfessionalForm, specialty: e.target.value })}
                placeholder="E.g.: Lash Specialist"
                data-testid="new-prof-specialty-input"
              />
            </div>
            <div>
              <Label className="mb-2 block">Professional Photo</Label>
              <ImageUpload
                folder="professionals"
                currentImageUrl={newProfessionalForm.imageUrl}
                onUploadComplete={(url) => setNewProfessionalForm({ ...newProfessionalForm, imageUrl: url })}
              />
            </div>
            <Button
              className="w-full bg-black text-[#e8dcc8]"
              onClick={handleAddProfessional}
              disabled={!newProfessionalForm.name || !newProfessionalForm.specialty || !newProfessionalForm.imageUrl}
              data-testid="save-new-professional-btn"
            >
              <Plus className="mr-2" /> Add Professional
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Product Edit Dialog */}
      <Dialog open={editProductDialog} onOpenChange={setEditProductDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="product-edit-dialog" aria-describedby="product-dialog-description">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Edit Product' : 'New Product'}</DialogTitle>
          </DialogHeader>
          <p id="product-dialog-description" className="sr-only">Form to create or edit a product</p>
          <div className="space-y-4">
            <div>
              <Label htmlFor="product-name">Name</Label>
              <Input 
                id="product-name"
                value={productForm.name}
                onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                data-testid="product-name-input"
              />
            </div>
            <div>
              <Label htmlFor="product-description">Description</Label>
              <Input 
                id="product-description"
                value={productForm.description}
                onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                data-testid="product-description-input"
              />
            </div>
            <div>
              <Label htmlFor="product-price">Price (AUD)</Label>
              <Input 
                id="product-price"
                type="number"
                value={productForm.price}
                onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                data-testid="product-price-input"
              />
            </div>
            <div>
              <Label className="mb-2 block">Product Image</Label>
              <ImageUpload
                folder="products"
                currentImageUrl={productForm.imageUrl}
                onUploadComplete={(url) => setProductForm({ ...productForm, imageUrl: url })}
              />
            </div>
            <Button 
              className="w-full bg-black text-[#e8dcc8]" 
              onClick={handleSaveProduct}
              disabled={!productForm.name || !productForm.description || !productForm.price}
              data-testid="save-product-btn"
            >
              {editingProduct ? 'Save Changes' : 'Create Product'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Testimonial Edit Dialog */}
      <Dialog open={editTestimonialDialog} onOpenChange={setEditTestimonialDialog}>
        <DialogContent data-testid="testimonial-edit-dialog" aria-describedby="testimonial-dialog-description">
          <DialogHeader>
            <DialogTitle>{editingTestimonial ? 'Edit Testimonial' : 'New Testimonial'}</DialogTitle>
          </DialogHeader>
          <p id="testimonial-dialog-description" className="sr-only">Form to create or edit a testimonial</p>
          <div className="space-y-4">
            <div>
              <Label htmlFor="testimonial-quote">Quote</Label>
              <textarea 
                id="testimonial-quote"
                className="w-full border rounded-md p-2 min-h-[100px]"
                value={testimonialForm.quote}
                onChange={(e) => setTestimonialForm({ ...testimonialForm, quote: e.target.value })}
                placeholder="The client's testimonial..."
                data-testid="testimonial-quote-input"
              />
            </div>
            <div>
              <Label htmlFor="testimonial-author">Author Name</Label>
              <Input 
                id="testimonial-author"
                value={testimonialForm.author}
                onChange={(e) => setTestimonialForm({ ...testimonialForm, author: e.target.value })}
                placeholder="E.g.: Maria G."
                data-testid="testimonial-author-input"
              />
            </div>
            <Button 
              className="w-full bg-black text-[#e8dcc8]" 
              onClick={handleSaveTestimonial}
              disabled={!testimonialForm.quote || !testimonialForm.author}
              data-testid="save-testimonial-btn"
            >
              {editingTestimonial ? 'Save Changes' : 'Create Testimonial'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Gift Card Option Dialog */}
      <Dialog open={editGiftCardDialog} onOpenChange={setEditGiftCardDialog}>
        <DialogContent data-testid="giftcard-option-edit-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingGiftCardOption?.isCustom ? 'Edit Custom Amount Gift Card' : `Edit ${editingGiftCardOption ? formatPrice(editingGiftCardOption.amount) : ''} Gift Card`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!editingGiftCardOption?.isCustom && (
              <div>
                <Label htmlFor="giftcard-amount">Amount (AUD) *</Label>
                <Input
                  id="giftcard-amount"
                  type="number"
                  value={giftCardOptionForm.amount}
                  onChange={(e) => setGiftCardOptionForm({ ...giftCardOptionForm, amount: e.target.value })}
                  placeholder="e.g., 50"
                  data-testid="giftcard-amount-input"
                />
              </div>
            )}
            
            <div>
              <Label htmlFor="giftcard-image">Gift Card Image *</Label>
              <ImageUpload
                folder="latinbody"
                currentImageUrl={giftCardOptionForm.imageUrl}
                onUploadComplete={(url) => {
                  setGiftCardOptionForm({ ...giftCardOptionForm, imageUrl: url });
                }}
              />
              <p className="text-xs text-gray-500 mt-1">Upload an image that will be displayed for this gift card option</p>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="giftcard-enabled"
                checked={giftCardOptionForm.enabled}
                onCheckedChange={(checked) => setGiftCardOptionForm({ ...giftCardOptionForm, enabled: checked })}
                data-testid="giftcard-enabled-checkbox"
              />
              <Label htmlFor="giftcard-enabled" className="cursor-pointer">
                Enable this option on the website
              </Label>
            </div>

            <Button 
              className="w-full bg-black text-[#e8dcc8]" 
              onClick={handleSaveGiftCardOption}
              disabled={!giftCardOptionForm.imageUrl || (!editingGiftCardOption?.isCustom && !giftCardOptionForm.amount)}
              data-testid="save-giftcard-option-btn"
            >
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
